// src/lib/arrayUtils.ts
// Utility functions for working with layers and groups
// Migrated from legacy ArrayUtil.js

import { sessionStorageUtil } from './storage';
import type { Layer } from '../types';

/**
 * Filter visible layers based on hidden layers in storage
 * @param layers - Array of layer objects
 * @returns Array of visible layers (not hidden)
 */
export function getVisibleLayers<T extends { LayerId: number }>(layers: T[]): T[]
{
  if (!layers || layers.length === 0)
  {
    return [];
  }

  const layersToHide = sessionStorageUtil.getLayersToHide();

  if (!layersToHide)
  {
    return layers;
  }

  return layers.filter(layer =>
  {
    const isHidden = layersToHide.some((hiddenLayer: any) => hiddenLayer.LayerId === layer.LayerId);
    return !isHidden;
  });
}

/**
 * Get comma-separated string of layer names
 * @param layers - Array of layer objects with Name or LayerName property
 * @returns Comma-separated layer names or "Not Set" if empty
 */
export function getLayersName(layers: Array<{ Name?: string; LayerName?: string }>): string
{
  if (!layers || layers.length === 0)
  {
    return 'Not Set';
  }

  const layerNames = layers
    .map(layer => layer.Name || layer.LayerName)
    .filter((name): name is string => !!name);

  return layerNames.length > 0 ? layerNames.join(', ') : 'Not Set';
}

/**
 * Get comma-separated string of group names
 * @param conversationGroups - Array of group objects with Name or GroupName property
 * @returns Comma-separated group names or "Not Set" if empty
 */
export function getGroupsNames(
  conversationGroups: Array<{ Name?: string; GroupName?: string }>,
): string
{
  if (!conversationGroups || conversationGroups.length === 0)
  {
    return 'Not Set';
  }

  const groupNames = conversationGroups
    .map(group => group.Name || group.GroupName)
    .filter((name): name is string => !!name);

  return groupNames.length > 0 ? groupNames.join(', ') : 'Not Set';
}

/**
 * Get layer by ID from all layers in storage
 * @param layerId - The layer ID to find
 * @returns The layer object or null if not found
 */
export function getLayerById(layerId: number | string): Layer | null
{
  const allLayers = sessionStorageUtil.getAllLayers();

  if (!allLayers || allLayers.length === 0)
  {
    return null;
  }

  const id = typeof layerId === 'string' ? parseInt(layerId, 10) : layerId;
  return allLayers.find((layer: Layer) => layer.LayerId === id) || null;
}

/**
 * Get layer color by ID
 * @param layerId - The layer ID
 * @returns The layer color hex string or null
 */
export function getLayerColor(layerId: number | string): string | null
{
  const layer = getLayerById(layerId);
  return layer ? layer.Color : null;
}

/**
 * Check if a layer is visible (not hidden)
 * @param layerId - The layer ID to check
 * @returns true if layer is visible, false if hidden
 */
export function isLayerVisible(layerId: number): boolean
{
  const layersToHide = sessionStorageUtil.getLayersToHide();

  if (!layersToHide)
  {
    return true;
  }

  return !layersToHide.some((hiddenLayer: any) => hiddenLayer.LayerId === layerId);
}

/**
 * Sort layers by name alphabetically
 * @param layers - Array of layer objects
 * @param ascending - Sort direction (default: true)
 * @returns Sorted array of layers
 */
export function sortLayersByName<T extends { Name?: string; LayerName?: string }>(
  layers: T[],
  ascending: boolean = true,
): T[]
{
  return [...layers].sort((a, b) =>
  {
    const nameA = (a.Name || a.LayerName || '').toLowerCase();
    const nameB = (b.Name || b.LayerName || '').toLowerCase();

    if (ascending)
    {
      return nameA.localeCompare(nameB);
    }
    else
    {
      return nameB.localeCompare(nameA);
    }
  });
}

/**
 * Group items by layer
 * @param items - Array of items with LayerId property
 * @returns Map of LayerId to array of items
 */
export function groupByLayer<T extends { LayerId: number }>(items: T[]): Map<number, T[]>
{
  const grouped = new Map<number, T[]>();

  items.forEach(item =>
  {
    const existing = grouped.get(item.LayerId) || [];
    grouped.set(item.LayerId, [...existing, item]);
  });

  return grouped;
}
